import React, { useMemo } from 'react'

import { HStack, Icon, T, Tooltip, VStack } from 'govwell-ui'
import { TimeZone } from 'types/graphql'

import { DateFormats, formatDateInTimeZone, formatSemanticDate } from 'src/utils/date'
import { formatFee } from 'src/utils/fee'

type SummarySectionProps = {
  accrualWindow: string | null
  estimatedDepositDate: string | null
  totalCents: number | null
  timeZone: TimeZone | null | undefined
  approvedAt: string | null
}

type SummaryItem = {
  id: string
  label: string
  tooltip?: string
  value: string
  emphasize?: boolean
}

const SummarySection = ({
  accrualWindow,
  estimatedDepositDate,
  totalCents,
  timeZone,
  approvedAt,
}: SummarySectionProps) => {
  const items = useMemo<SummaryItem[]>(() => {
    const formattedDepositDate = estimatedDepositDate
      ? formatSemanticDate(estimatedDepositDate)
      : '—'
    return [
      {
        id: 'accrual-window',
        label: 'Accrual window',
        value: accrualWindow || '—',
      },
      {
        id: 'deposit-date',
        label: 'Estimated deposit date',
        tooltip:
          'Deposit date is estimated by GovWell and may be affected by bank holidays and variations in processing times.',
        value: formattedDepositDate,
      },
      {
        id: 'total',
        label: 'Net total',
        value: typeof totalCents === 'number' ? formatFee(totalCents) : '—',
        emphasize: true,
      },
      {
        id: 'approved-on',
        label: 'Approved on',
        value: approvedAt
          ? formatDateInTimeZone(approvedAt, timeZone || undefined, DateFormats.MonthNameDateTime)
          : '—',
      },
    ]
  }, [accrualWindow, approvedAt, estimatedDepositDate, timeZone, totalCents])

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
      {items.map((item) => (
        <div
          key={item.id}
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          data-testid={`summary-${item.id}`}
        >
          <VStack align="start" gap={1}>
            <HStack align="center" gap={1}>
              <T.Label className="tracking-wide text-gray-500 uppercase">{item.label}</T.Label>
              {item.tooltip ? (
                <Tooltip content={item.tooltip}>
                  <span className="text-gray-500">
                    <Icon.FaQuestionCircle />
                  </span>
                </Tooltip>
              ) : null}
            </HStack>
            <T.BodyLarge
              className={item.emphasize ? 'font-semibold text-neutral-900' : 'text-neutral-900'}
            >
              {item.value}
            </T.BodyLarge>
          </VStack>
        </div>
      ))}
    </div>
  )
}

export default React.memo(SummarySection)
